import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export function CompanyDetectCard() {
  const { reload } = useCompany();
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/admin/companies/detect-all").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (s?.status !== "running") return undefined;
    const t = setInterval(() => { load(); reload(); }, 4000);
    return () => clearInterval(t);
  }, [s?.status, load, reload]);
  const start = async (force = false) => {
    try { await api.post(`/admin/companies/detect-all${force ? "?force=true" : ""}`); toast.success("Analyse lancée"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (enabled) => {
    try { await api.put("/admin/companies/auto", { enabled }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!s) return null;
  const running = s.status === "running";
  const st = s.stats || {};
  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="company-detect-card">
      <div className="flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-[#1E3A8A] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">Répartition automatique par l'IA</div>
          <p className="text-sm text-slate-600 mt-0.5">L'IA lit chaque document (destinataire, SIRET, adresse) et le range dans la bonne société
            lorsqu'elle est sûre (80 % ou plus). Sinon, elle propose la société sur le document et vous validez d'un clic.
            Les documents que vous avez rangés vous-même dans un dossier ne sont jamais déplacés sans votre accord.
            Renseignez le SIRET et l'adresse de chaque société ci-dessous : c'est indispensable pour distinguer des sociétés aux noms proches.</p>
        </div>
        <label className="flex items-center gap-2 text-sm shrink-0">
          <Switch checked={!!s.auto_company} onCheckedChange={toggle} data-testid="company-auto-switch" />Nouveaux documents
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => start(false)} disabled={running || !s.remaining} data-testid="company-detect-all-btn">
          {running && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Répartir les documents existants
        </Button>
        <Button variant="outline" onClick={() => start(true)} disabled={running || !s.recheckable} data-testid="company-detect-force-btn"
          title="À utiliser après avoir ajouté des SIRET ou adresses : revérifie les documents dont la société n'a pas été trouvée ou était déjà bonne">
          Tout revérifier
        </Button>
        <span className="text-sm text-slate-600" data-testid="company-detect-status">
          {running ? `Analyse en cours : ${s.done || 0} / ${s.total || "…"}`
            : `${s.remaining} document(s) jamais vérifié(s)`}
          {(st.moved || st.suggested) ? ` · dernière analyse : ${st.moved || 0} déplacé(s), ${st.suggested || 0} à vérifier` : ""}
          {s.status === "interrupted" ? " · interrompue, relancez-la" : ""}
        </span>
      </div>
    </div>
  );
}
